import { DeviceHealth, GlobalAdmin } from "coolremote-sdk";
import { Thunk, thunk } from "easy-peasy";
import {
  ICreateSubscriptionResult,
  IDeviceHealthFilterOption,
  IDeviceHealthFilters,
  IDeviceHealthPage,
  IEntityRef,
  IHealthSubscription,
  INewSubscription,
  ISelectedFilters,
  ISiteNameIndex
} from "./DeviceHealth.types";

/**
 * Device Health dashboard data layer (CM-208), against the CM-207 admin API.
 *
 * The API accepts multi-value filters and guarantees a deterministic sort
 * (customerName, siteName, serial, _id), so this is a straight pass-through with
 * real server-side pagination - no client-side filtering, sorting or paging.
 *
 * The panels cascade, so a site selection already excludes the sites of
 * unselected customers, and a customer selection excludes unselected territories.
 * That means we only need to send the *deepest* dimension that is a strict subset
 * of what it offers - it fully expresses the scope, and keeps the query string to
 * one list of ids instead of three.
 */

/**
 * The ids to send for one dimension: nothing when the panel offers no options
 * (it can't constrain anything) or when every option is selected (no narrowing).
 */
const narrowingIds = (selected: string[], optionCount: number) => {
  if (!optionCount || selected.length >= optionCount) {
    return undefined;
  }

  return selected;
};

export interface IDeviceHealthModel {
  getFilters: Thunk<IDeviceHealthModel, void, any, any, Promise<IDeviceHealthFilters>>;
  getReport: Thunk<
    IDeviceHealthModel,
    {
      filters: ISelectedFilters;
      /**
       * How many options each panel actually offers. A panel with no options at
       * all can't constrain anything - without this, an empty option list would
       * be indistinguishable from "the user cleared this panel" and would blank
       * the table.
       */
      optionCounts: { salesTerritories: number; customers: number; sites: number };
      page: number;
      pageSize: number;
      reportDate: string | null;
    },
    any,
    any,
    Promise<IDeviceHealthPage>
  >;
  getSiteNameIndex: Thunk<IDeviceHealthModel, void, any, any, Promise<ISiteNameIndex>>;
  getAllCustomers: Thunk<IDeviceHealthModel, void, any, any, Promise<IEntityRef[]>>;
  getCustomerSites: Thunk<IDeviceHealthModel, string, any, any, Promise<IEntityRef[]>>;
  getSubscriptions: Thunk<IDeviceHealthModel, void, any, any, Promise<IHealthSubscription[]>>;
  createSubscriptions: Thunk<
    IDeviceHealthModel,
    INewSubscription,
    any,
    any,
    Promise<ICreateSubscriptionResult>
  >;
  deleteSubscription: Thunk<IDeviceHealthModel, string, any, any, Promise<any>>;
}

export const deviceHealthModel: IDeviceHealthModel = {
  getFilters: thunk(async () => {
    const data = await DeviceHealth.getFilters();

    // Territories stay as raw enum values here. The screen resolves them through
    // the store's regionTypesMirror, which is reactive - resolving them at fetch
    // time races with getServiceTypes() and leaves bare numbers on screen.
    const salesTerritories: IEntityRef[] = (data?.salesTerritories || []).map((value: number) => ({
      id: String(value),
      name: String(value)
    }));

    // `salesTerritory` here is what lets the territory -> customer panel cascade.
    const customers: IDeviceHealthFilterOption[] = (data?.customers || []).map((customer: any) => ({
      id: customer.id,
      name: (customer.name || "").trim() || customer.id,
      salesTerritory:
        customer.salesTerritory === undefined || customer.salesTerritory === null
          ? undefined
          : String(customer.salesTerritory)
    }));

    const sites: IDeviceHealthFilterOption[] = (data?.sites || []).map((site: any) => ({
      id: site.id,
      name: (site.name || "").trim() || site.id,
      customer: site.customerId
    }));

    return {
      reportDate: data?.reportDate || null,
      salesTerritories,
      customers,
      sites
    };
  }),

  getReport: thunk(async (_actions, { filters, optionCounts, page, pageSize, reportDate }) => {
    const {
      salesTerritories = [],
      customers: selectedCustomers = [],
      sites: selectedSites = []
    } = filters || ({} as ISelectedFilters);

    const territoryConstrains = optionCounts.salesTerritories > 0;
    const customerConstrains = optionCounts.customers > 0;
    const siteConstrains = optionCounts.sites > 0;

    // A panel that offers options but has none selected means nothing can match.
    const clearedPanel =
      (territoryConstrains && !salesTerritories.length) ||
      (customerConstrains && !selectedCustomers.length) ||
      (siteConstrains && !selectedSites.length);

    if (clearedPanel) {
      return { records: [], totalCount: 0, page, pageSize, reportDate };
    }

    // Deepest narrowing dimension only - see the note at the top of the file.
    const siteId = narrowingIds(selectedSites, optionCounts.sites);
    const customerId = siteId ? undefined : narrowingIds(selectedCustomers, optionCounts.customers);
    const salesTerritory =
      siteId || customerId ? undefined : narrowingIds(salesTerritories, optionCounts.salesTerritories);

    const data = await DeviceHealth.getReports({
      pageNum: page + 1,
      pageSize,
      siteId,
      customerId,
      salesTerritory,
      reportDate: reportDate || undefined
    });

    return {
      records: data?.reports || [],
      totalCount: data?.totalCount || 0,
      page,
      pageSize,
      reportDate: data?.reportDate || reportDate || null
    };
  }),

  /**
   * Names for every site in the system, for the subscriptions popup.
   *
   * Not derived from the report filters on purpose: those only list sites that
   * have rows in the current snapshot (17 of 317 on stg), whereas a subscription
   * may point at any site - which is why unresolved rows showed up as "-".
   * `/admins/sites` already denormalises customerName, so one call covers both
   * columns.
   */
  getSiteNameIndex: thunk(async () => {
    const sites = await GlobalAdmin.getAllSites();

    return Object.entries(sites || {}).reduce((acc: ISiteNameIndex, [key, site]: [string, any]) => {
      acc[site?.id || key] = {
        siteName: (site?.name || "").trim(),
        customerName: (site?.customerName || "").trim()
      };

      return acc;
    }, {});
  }),

  /**
   * Every customer in the system, for the "register for site updates" popup.
   *
   * Same reasoning as getSiteNameIndex: you must be able to subscribe to any
   * customer, not only the handful that happen to have rows in today's report.
   * `/admins/customers/lists/reduced` is 404 on the server, so this uses the
   * customers search endpoint with an empty term.
   */
  getAllCustomers: thunk(async () => {
    const customers = await GlobalAdmin.getCustomersByCustomerName("");

    return Object.entries(customers || {})
      .map(([key, customer]: [string, any]) => ({
        id: customer?.id || key,
        name: (customer?.name || "").trim() || customer?.id || key
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }),

  /** The sites of one customer, loaded when that customer is picked. */
  getCustomerSites: thunk(async (_actions, customerId) => {
    const sites = await GlobalAdmin.getCustomerSites(customerId);

    return Object.entries(sites || {})
      .map(([key, site]: [string, any]) => ({
        id: site?.id || key,
        name: (site?.name || "").trim() || site?.id || key
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }),

  getSubscriptions: thunk(async () => {
    const data = await DeviceHealth.getSubscriptions();

    return data || [];
  }),

  createSubscriptions: thunk(async (_actions, payload) => {
    return DeviceHealth.createSubscriptions(payload);
  }),

  deleteSubscription: thunk(async (_actions, subscriptionId) => {
    return DeviceHealth.deleteSubscription(subscriptionId);
  })
};
